import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button,
  Col,
  Input,
  Row,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { addFlashMessage } from '../../../../../actions/App';
import { fetchUserProfile } from '../../../../../actions/Auth';

import { APIgetWatermarkPreview, APIupdateSettings } from '../../../../../api/Settings';

import SettingsEditButtons from '../../EditButtons';
import LogoUploader from '../../../LogoUploader';
import TrialNotice from '../../../../TrialNotice';
import UpgradePlanButton from '../../../../UpgradePlanButton';

import { userHasFeature } from '../../../../../helpers/Features';

const SettingsWatermark = ({
  dispatch,
  settings,
  client,
}) => {
  const [watermarkUrl, setWatermarkUrl] = useState(client.watermark_url);
  const [watermarkPreviewUrl, setWatermarkPreviewUrl] = useState(client.watermark_url);
  const [watermarkPreviewImageUrl, setWatermarkPreviewImageUrl] = useState(null);
  const [watermarkPosition, setWatermarkPosition] = useState(settings.watermark_position);
  const [isWatermarkPositionDirty, setIsWatermarkPositionDirty] = useState(false);
  const [isWatermarkDirty, setIsWatermarkDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [watermarkPreviewImageLoading, setWatermarkPreviewImageLoading] = useState(false);
  const [unsaved, setUnsaved] = useState(false);

  const intl = useIntl();

  const handleSaveSettings = () => {
    setSaving(true);
    const settingsToUpdate = {};
    if (isWatermarkDirty) {
      settingsToUpdate.watermark_path = watermarkUrl;
    }
    if (isWatermarkPositionDirty) {
      settingsToUpdate.watermark_position = watermarkPosition;
    }
    APIupdateSettings(settingsToUpdate).then(() => {
      dispatch(fetchUserProfile()).then(() => {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Changes have been saved' }),
        });
        setSaving(false);
        setIsWatermarkDirty(false);
      });
    });
  };

  useEffect(() => {
    setWatermarkPreviewImageLoading(true);
    APIgetWatermarkPreview(watermarkUrl, watermarkPosition).then(((response) => {
      setWatermarkPreviewImageUrl(response.data.url);
      setWatermarkPreviewImageLoading(false);
    }));
  }, [watermarkUrl, watermarkPosition]);

  return (
    <>
      <div className="settings-inner-content">
        <Row>
          <Col>
            <h3>{intl.formatMessage({ defaultMessage: 'Watermark' })}
              <UpgradePlanButton
                feature="watermarking"
              />
            </h3>
          </Col>
        </Row>
        <TrialNotice feature="watermarking" className="pl-0" />
        <Row className="logo-row watermark-row">
          <Col xl={6}>
            <h4>
              {intl.formatMessage({ defaultMessage: 'Watermark image' })}
            </h4>
            <p className="watermark-tooltip">
              {intl.formatMessage({
                defaultMessage: 'Transparent PNG files with a 50-70% opacity are suggested',
              })}
            </p>
            <div className="image-content-wrapper">
              <div className="image-container watermark-container">
                <div className="image-wrapper">
                  {watermarkPreviewUrl ? (
                    <img src={watermarkPreviewUrl} alt="Watermark" />
                  ) : (
                    <p className="center">
                      {intl.formatMessage({
                        defaultMessage: 'Upload a file to be used as a watermark on your images.',
                      })}
                    </p>
                  )}
                </div>
              </div>
              {userHasFeature('watermarking') && (
                <div className="logo-buttons">
                  <LogoUploader
                    name="watermark-uploader"
                    path="watermark"
                    uploadButtonIcon={watermarkUrl ? 'fa-random' : 'fa-upload'}
                    uploadButtonText={
                    watermarkUrl ?
                      intl.formatMessage({ defaultMessage: 'Change' }) :
                      intl.formatMessage({ defaultMessage: 'Upload' })
                  }
                    handleChangeImage={(url) => {
                      setWatermarkUrl(url);
                      setIsWatermarkDirty(true);
                      setUnsaved(true);
                    }}
                    handleChangePreview={(url) => {
                      setWatermarkPreviewUrl(url);
                    }}
                  />
                  {watermarkUrl && (
                    <>
                      <Button
                        onClick={() => {
                          setIsWatermarkDirty(true);
                          setWatermarkUrl(null);
                          setWatermarkPreviewUrl(null);
                        }}
                        id="WatermarkRemoveButton"
                        color="link"
                      >
                        <i className="far fa-trash fa-fw" />
                      </Button>
                      <UncontrolledTooltip target="WatermarkRemoveButton" placement="bottom">
                        {intl.formatMessage({ defaultMessage: 'Remove' })}
                      </UncontrolledTooltip>
                    </>
                  )}
                </div>
              )}
            </div>
          </Col>
          {watermarkPreviewUrl && (
          <Col xl={6}>
            <h4>{intl.formatMessage({ defaultMessage: 'Watermark placement' })}</h4>
            <p>
              {intl.formatMessage({ defaultMessage: 'Set your watermark position' })}
            </p>
            <div className="image-content-wrapper watermark-content-wrapper">
              <div className="image-container watermark-preview-container">
                <div className="image-wrapper">
                  {watermarkPreviewImageLoading && (
                  <div className="watermark-preview-loading">
                    <i className="fa-solid fa-spinner fa-2x fa-spin" />
                  </div>
                  )}
                  {watermarkPreviewImageUrl && (
                    <img
                      src={watermarkPreviewImageUrl}
                      alt={intl.formatMessage({ defaultMessage: 'Watermark preview' })}
                    />
                  )}
                </div>
              </div>
              {userHasFeature('watermarking') && (
                <div className="watermark-position-select">
                  <Input
                    type="select"
                    className="custom-select"
                    name="watermark-position"
                    bsSize="sm"
                    defaultValue={watermarkPosition}
                    disabled={watermarkPreviewImageLoading}
                    onChange={(e) => {
                      setWatermarkPosition(e.target.value);
                      setIsWatermarkPositionDirty(true);
                      setUnsaved(true);
                    }}
                  >
                    {Object.keys(settings.allowed_watermark_positions)
                      .map(key => (
                        <option key={key} value={key}>
                          {settings.allowed_watermark_positions[key]}
                        </option>
                      ))}
                  </Input>
                </div>
              )}
            </div>
          </Col>
          )}
        </Row>
      </div>
      <SettingsEditButtons
        saving={saving}
        handleSave={handleSaveSettings}
        unsaved={unsaved}
      />
    </>
  );
};

const mapStateToProps = state => ({
  client: state.Auth.profile?.client,
  settings: state.App.settings,
});

SettingsWatermark.propTypes = {
  dispatch: PropTypes.func.isRequired,
  client: PropTypes.object.isRequired,
  settings: PropTypes.object.isRequired,
};

export default connect(mapStateToProps)(SettingsWatermark);
